// View model for a framework readiness block (ENTRAID-307). Pure. The text is always present: tone only styles it.

/** One muted line shown under the readiness cards. Readiness is a comparison, never a certification. */
export const READINESS_NOTE = 'Readiness compares your tenant configuration with each benchmark. It is not a certification.';

const LEVELS = Object.freeze({
  ready: { label: 'Benchmark ready', tone: 'ok' },
  nearly_ready: { label: 'Nearly benchmark ready', tone: 'warn' },
  not_ready: { label: 'Not benchmark ready', tone: 'danger' },
});

const REASONS = Object.freeze({
  license_missing: 'License missing', source_disabled: 'Data source not enabled in Settings', collection_failed: 'Collection failed',
  no_data: 'No data', permission_missing: 'Permission missing', check_error: 'Check error', evidence_stale: 'Evidence out of date',
  partial_data: 'Partial data', activation_state_unknown: 'Activation state unknown', no_collector_yet: 'Collector not released', out_of_scope: 'Not covered', failed: 'Failed', partial: 'Partial',
  not_assessed: 'Not assessed', expired: 'Attestation expired', attested: 'Attested',
  attestation_required: 'Requires attestation', attestation_expired: 'Attestation expired',
});

/** Short reader label for an open-control reason code; anything outside the allowlist is "Other". */
export const reasonLabel = (code) => (typeof code === 'string' && Object.hasOwn(REASONS, code) ? REASONS[code] : 'Other');

/** The short action line under the level. Text always carries the level: the accent color only supports it. */
const ACTION = Object.freeze({ ready: 'No action required', nearly_ready: 'Review recommended', not_ready: 'Review the open controls', no_report: 'Generate a report' });

const MET = ['passed', 'attested'];
const num = (n) => (Number.isFinite(n) ? n : 0);
const sumOf = (counts, keys) => keys.reduce((n, k) => n + num(counts?.[k]), 0);

/**
 * Criteria met (ENTRAID-330): { met, inScope, percent }. Formula: percent = floor(100 * met / inScope), where inScope is every
 * control in the framework scope (coverage.total, else scope.controlCount, else the sum of the verdict counts), so controls that
 * are not assessed, not covered or awaiting attestation stay in the denominator and never inflate readiness. met is the controls
 * that passed or are attested: from the verdict counts when present (passed + attested), otherwise from coverage and the open
 * controls (assessedAutomatically + valid attestations, minus open controls whose verdict is failed or partial).
 * Rounded down, so 100% always means every in-scope control is met. Returns null when inScope is 0 (no percentage is shown).
 */
export function criteriaMet({ counts, coverage, scope, readiness } = {}) {
  const hasCounts = counts && typeof counts === 'object' && Object.keys(counts).length > 0;
  const inScope = num(coverage?.total) || num(scope?.controlCount) || (hasCounts ? Object.values(counts).reduce((n, v) => n + num(v), 0) : 0);
  if (!(inScope > 0)) return null;
  let met;
  if (hasCounts) {
    met = sumOf(counts, MET);
  } else {
    const done = num(coverage?.assessedAutomatically) + num(coverage?.byAttestation?.valid);
    const bad = (Array.isArray(readiness?.open) ? readiness.open : []).filter((o) => ['failed', 'partial'].includes(o?.verdict ?? o?.reason)).length;
    met = Math.max(0, done - bad);
  }
  met = Math.min(met, inScope);
  return { met, inScope, percent: Math.floor((met / inScope) * 100) };
}

/** Card styling shared by the Overview card and the Compliance strip: a neutral card with a small left accent. Never a red fill. */
export const READINESS_CARD = 'rounded-lg border border-slate-200 border-l-4 bg-white text-slate-800';
export const READINESS_ACCENT = Object.freeze({
  ready: 'border-l-emerald-600',
  nearly_ready: 'border-l-slate-400',
  not_ready: 'border-l-amber-500',
  no_report: 'border-l-slate-300',
});

const count = (n) => (Number.isFinite(n) ? n : 0);
const str = (v) => (typeof v === 'string' ? v : '');

export function readinessView(framework) {
  const f = framework ?? {};
  const name = `${str(f.name) || str(f.id)} ${str(f.version)}`.trim();
  const scopeName = str(f.scope?.name) || str(f.scope?.id);
  const r = f.readiness;
  let level;
  let label;
  let tone;
  if (!r || typeof r !== 'object') {
    level = 'no_report';
    label = 'No report yet';
    tone = 'warn';
  } else if (r.basis === 'nothing_assessed') {
    level = 'not_ready';
    label = 'Nothing assessed yet';
    tone = 'warn';
  } else {
    level = Object.hasOwn(LEVELS, r.level) ? r.level : 'not_ready';
    ({ label, tone } = LEVELS[level]);
  }
  const open = Array.isArray(r?.open) ? r.open.filter((o) => o && typeof o.controlId === 'string') : [];
  const openText = open.length === 0 ? '' : `Open controls (${open.length}): ${open.map((o) => `${o.controlId} (${reasonLabel(o.reason)})`).join(', ')}`;
  const c = f.coverage;
  const coverageText = c && typeof c === 'object'
    ? `${count(c.assessedAutomatically)} assessed automatically, ${count(c.byAttestation?.total)} by attestation (${count(c.byAttestation?.valid)} current), ${Array.isArray(c.notCovered) ? c.notCovered.length : 0} not covered, of ${count(c.total)} in scope`
    : `${count(f.scope?.controlCount)} controls in scope`;
  const cm = criteriaMet(f);
  const percent = cm ? cm.percent : null;
  const percentText = cm ? `${cm.percent}% of criteria met (${cm.met} of ${cm.inScope})` : '';
  return { level, label, tone, percent, percentText, action: ACTION[level] ?? ACTION.no_report, headline: `${name}, ${scopeName}: ${label}`, openText, coverageText };
}
